(() => {
  const cartUrl = (path) => `${window.Shopify?.routes?.root || '/'}${path}`;

  const escapeHTML = (value = '') => String(value).replace(/[&<>"']/g, (char) => ({
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;'
  }[char]));

  const formatMoney = (cents, currency) => {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency: currency || window.Shopify?.currency?.active || 'USD',
        maximumFractionDigits: cents % 100 ? 2 : 0
      }).format(cents / 100);
    } catch {
      return `$${(cents / 100).toFixed(cents % 100 ? 2 : 0)}`;
    }
  };

  const getCart = () => fetch(cartUrl('cart.js'), { credentials: 'same-origin' }).then((response) => {
    if (!response.ok) throw new Error('Cart refresh failed');
    return response.json();
  });

  const changeCartItem = (key, quantity) => fetch(cartUrl('cart/change.js'), {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
    body: JSON.stringify({ id: key, quantity })
  }).then((response) => {
    if (!response.ok) throw new Error('Cart update failed');
    return response.json();
  });

  const addCartItem = (form) => fetch(cartUrl('cart/add.js'), {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Accept': 'application/json' },
    body: new FormData(form)
  });

  const closeAfterAnimation = (dialog) => {
    if (!dialog.open) return;

    if (matchMedia('(prefers-reduced-motion: reduce)').matches) {
      dialog.close();
      return;
    }

    dialog.classList.add('is-closing');
    setTimeout(() => dialog.open && dialog.close(), dialog.matches('[data-cart-drawer]') ? 260 : 600);
  };

  const initHeader = (header) => {
    if (!header || header.dataset.initialized) return;
    header.dataset.initialized = 'true';

    const mobileMenu = header.querySelector('[data-mobile-menu]');
    const searchDialog = header.querySelector('[data-search-dialog]');
    const cartDrawer = header.querySelector('[data-cart-drawer]');
    const cartItems = header.querySelector('[data-cart-items]');
    const cartEmpty = header.querySelector('[data-cart-empty]');
    const cartFooter = header.querySelector('[data-cart-footer]');
    const cartLoading = header.querySelector('[data-cart-loading]');
    const cartSubtotal = header.querySelector('[data-cart-subtotal]');
    const cartDrawerCount = header.querySelector('[data-cart-drawer-count]');
    let returnFocus;
    let megaCloseTimer;

    const openDialog = (dialog, trigger) => {
      if (!dialog || dialog.open) return;
      returnFocus = trigger;
      dialog.showModal();
    };

    const closeDialog = (dialog, animated = false) => {
      if (!dialog?.open) return;
      if (animated) closeAfterAnimation(dialog);
      else dialog.close();
    };

    const updateCartCount = (count) => {
      document.querySelectorAll('[data-cart-count], [data-cart-drawer-count]').forEach((element) => {
        element.textContent = count;
      });
    };

    const setCartLoading = (isLoading) => {
      cartDrawer?.toggleAttribute('aria-busy', isLoading);
      if (cartItems) cartItems.hidden = isLoading;
      if (cartLoading) cartLoading.hidden = !isLoading;
      if (cartEmpty && isLoading) cartEmpty.hidden = true;
      if (cartFooter && isLoading) cartFooter.hidden = true;
    };

    const cartLineHTML = (item, currency) => {
      const image = item.image
        ? `<img src="${escapeHTML(item.image)}" alt="${escapeHTML(item.product_title)}" loading="lazy">`
        : '';
      const details = [
        item.variant_title && item.variant_title !== 'Default Title' ? item.variant_title : '',
        item.selling_plan_allocation?.selling_plan?.name || ''
      ].filter(Boolean).map((detail) => `<p>${escapeHTML(detail)}</p>`).join('');

      return `
        <article class="cart-drawer__item">
          <a class="cart-drawer__media" href="${escapeHTML(item.url)}">${image}</a>
          <div class="cart-drawer__item-content">
            <div class="cart-drawer__item-heading">
              <a href="${escapeHTML(item.url)}">${escapeHTML(item.product_title)}</a>
              <strong>${formatMoney(item.final_line_price, currency)}</strong>
            </div>
            ${details}
            <div class="cart-drawer__item-actions">
              <div class="cart-drawer__quantity" aria-label="Quantity">
                <button type="button" data-cart-quantity data-key="${escapeHTML(item.key)}" data-quantity="${Math.max(0, item.quantity - 1)}" aria-label="Decrease quantity">-</button>
                <span>${item.quantity}</span>
                <button type="button" data-cart-quantity data-key="${escapeHTML(item.key)}" data-quantity="${item.quantity + 1}" aria-label="Increase quantity">+</button>
              </div>
              <button type="button" class="cart-drawer__remove" data-cart-quantity data-key="${escapeHTML(item.key)}" data-quantity="0" aria-label="Remove">
                <span aria-hidden="true"></span>
              </button>
            </div>
          </div>
        </article>
      `;
    };

    const renderCart = (cart) => {
      const hasItems = cart.item_count > 0;
      setCartLoading(false);
      updateCartCount(cart.item_count);
      if (cartDrawerCount) cartDrawerCount.textContent = cart.item_count;
      if (cartItems) cartItems.innerHTML = cart.items.map((item) => cartLineHTML(item, cart.currency)).join('');
      if (cartEmpty) cartEmpty.hidden = hasItems;
      if (cartFooter) cartFooter.hidden = !hasItems;
      if (cartSubtotal) cartSubtotal.textContent = formatMoney(cart.total_price, cart.currency);
    };

    const refreshCart = () => getCart().then((cart) => {
      renderCart(cart);
      return cart;
    });

    const openCartDrawer = (trigger) => {
      closeDialog(mobileMenu);
      closeDialog(searchDialog);
      setCartLoading(true);
      openDialog(cartDrawer, trigger);
      return refreshCart();
    };

    header.unfoundOpenCart = (cart, trigger) => {
      closeDialog(mobileMenu);
      closeDialog(searchDialog);
      if (cart) renderCart(cart);
      openDialog(cartDrawer, trigger);
    };

    header.unfoundSetCartLoading = setCartLoading;

    header.unfoundCloseCart = () => closeDialog(cartDrawer);

    header.querySelector('[data-mobile-menu-open]')?.addEventListener('click', (event) => {
      closeDialog(cartDrawer);
      closeDialog(searchDialog);
      openDialog(mobileMenu, event.currentTarget);
    });

    header.querySelector('[data-mobile-menu-close]')?.addEventListener('click', () => {
      closeDialog(mobileMenu, true);
    });

    header.querySelectorAll('[data-search-open]').forEach((button) => {
      button.addEventListener('click', () => {
        const focusTarget = mobileMenu.open ? returnFocus : button;
        closeDialog(mobileMenu);
        closeDialog(cartDrawer);
        openDialog(searchDialog, focusTarget);
        requestAnimationFrame(() => searchDialog.querySelector('[data-search-input]')?.focus());
      });
    });

    header.querySelector('[data-search-close]')?.addEventListener('click', () => {
      closeDialog(searchDialog);
    });

    header.querySelectorAll('[data-cart-open]').forEach((link) => {
      link.addEventListener('click', (event) => {
        event.preventDefault();
        openCartDrawer(link).catch(() => {
          window.location.href = link.href;
        });
      });
    });

    header.querySelector('[data-cart-close]')?.addEventListener('click', () => {
      closeDialog(cartDrawer, true);
    });

    cartDrawer?.addEventListener('click', (event) => {
      const button = event.target.closest('[data-cart-quantity]');
      if (!button) return;

      button.disabled = true;
      setCartLoading(true);
      changeCartItem(button.dataset.key, Number(button.dataset.quantity))
        .then(renderCart)
        .catch(() => refreshCart())
        .finally(() => {
          setCartLoading(false);
          button.disabled = false;
        });
    });

    header.querySelectorAll('.site-header__link-item--nested').forEach((item) => {
      const openMega = () => {
        clearTimeout(megaCloseTimer);
        item.classList.add('is-mega-open');
      };
      const closeMega = () => {
        clearTimeout(megaCloseTimer);
        megaCloseTimer = setTimeout(() => item.classList.remove('is-mega-open'), 260);
      };

      item.addEventListener('mouseenter', openMega);
      item.addEventListener('mouseleave', closeMega);
      item.addEventListener('focusin', openMega);
      item.addEventListener('focusout', closeMega);
    });

    [mobileMenu, searchDialog, cartDrawer].forEach((dialog) => {
      if (!dialog) return;

      dialog.addEventListener('cancel', (event) => {
        if (dialog === mobileMenu) {
          event.preventDefault();
          closeDialog(dialog, true);
        }
      });

      dialog.addEventListener('click', (event) => {
        if (event.target === dialog) closeDialog(dialog, dialog !== searchDialog);
      });

      dialog.addEventListener('close', () => {
        dialog.classList.remove('is-closing');
        if (!mobileMenu.open && !searchDialog.open && !cartDrawer.open) {
          returnFocus?.focus();
        }
      });
    });
  };

  const updateScrollState = () => {
    document.querySelectorAll('[data-site-header]').forEach((header) => {
      header.classList.toggle('is-scrolled', scrollY > 24);
    });
  };

  const initAll = (root = document) => {
    root.querySelectorAll?.('[data-site-header]').forEach(initHeader);
    updateScrollState();
  };

  initAll();

  if (!window.unfoundHeaderEventsBound) {
    window.unfoundHeaderEventsBound = true;
    addEventListener('scroll', updateScrollState, { passive: true });
    document.addEventListener('shopify:section:load', (event) => initAll(event.target));
    document.addEventListener('submit', (event) => {
      const form = event.target.closest('form[action*="/cart/add"]');
      const header = document.querySelector('[data-site-header]');
      if (!form || !header) return;

      event.preventDefault();
      const button = event.submitter || form.querySelector('[type="submit"]');
      let itemAdded = false;
      if (button) button.disabled = true;
      header.unfoundSetCartLoading?.(true);
      header.unfoundOpenCart?.(null, button);

      addCartItem(form)
        .then((response) => {
          if (!response.ok) throw new Error('Cart add failed');
          itemAdded = true;
          return getCart();
        })
        .then((cart) => {
          header.unfoundOpenCart?.(cart, button);
        })
        .catch(() => {
          header.unfoundSetCartLoading?.(false);
          header.unfoundCloseCart?.();
          if (itemAdded) window.location.href = cartUrl('cart');
          else HTMLFormElement.prototype.submit.call(form);
        })
        .finally(() => {
          if (button) button.disabled = false;
        });
    });
  }
})();
